"use client";

import { useState, useEffect, useRef, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../hooks/useAuth";
import { commentsAPI, socialUtils, handleApiError, likesAPI } from "../../services/api";
import StarRating from "../social/posts/StarRating";
import { getUserTypeColor } from "../../utils/userTypeUtils";
import { showErrorToast } from "../../utils/notificationUtils";
import { getUserPlaceholderImage } from "../../utils/imageUtils";
import ConfirmModal from "../channels/modals/ConfirmModal";
import "./ChannelPostComments.css";

// Helper: transform raw API comments to component format
const transformCommentList = (rawComments) => {
  return rawComments
    .map(socialUtils.transformCommentData)
    .filter(comment => comment !== null)
    .map(comment => ({
      id: comment.id,
      content: comment.text,
      author: {
        id: comment.user?.id || comment.user?.user_id,
        name: comment.user.name,
        username: comment.user.name,
        profileImage: comment.user.avatar
      },
      user_id: comment.user?.id || comment.user?.user_id,
      createdAt: comment.timestamp,
      likes: comment.total_reactions || 0,
      user_reaction_type: comment.user_reaction_type || null,
      reaction_counts: comment.reaction_counts || {},
      total_reactions: comment.total_reactions || 0,
      replies_count: comment.replies_count || 0
    }));
};

const ChannelPostComments = ({ post, onCommentAdded, highlightCommentId }) => {
  const { t } = useTranslation();
  const { getProfileImageUrl, getUserType, user: currentUser } = useAuth();

  const [comments, setComments] = useState([]);
  const [commentText, setCommentText] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showReactionPicker, setShowReactionPicker] = useState({});
  const [reactingComments, setReactingComments] = useState({});
  const timeoutRefs = useRef({});
  const containerRefs = useRef({});

  // Reply state management
  const [showReplyInput, setShowReplyInput] = useState({});
  const [replyInputs, setReplyInputs] = useState({});
  const [submittingReplies, setSubmittingReplies] = useState({});
  const [replies, setReplies] = useState({});
  const [showReplies, setShowReplies] = useState({});
  const [loadingReplies, setLoadingReplies] = useState({});

  // Reply reaction state management
  const [showReplyReactionPicker, setShowReplyReactionPicker] = useState({});
  const [reactingReplies, setReactingReplies] = useState({});
  const replyTimeoutRefs = useRef({});
  const replyContainerRefs = useRef({});

  // Delete state management
  const [deletingComments, setDeletingComments] = useState({});
  const [deletingReplies, setDeletingReplies] = useState({});

  // Modal state management
  const [deleteCommentModal, setDeleteCommentModal] = useState({ commentId: null, isOpen: false });
  const [deleteReplyModal, setDeleteReplyModal] = useState({ replyId: null, commentId: null, isOpen: false });
  const [errorModal, setErrorModal] = useState({ isOpen: false, message: '' });

  // Get current user type color
  const currentUserType = getUserType();
  const userTypeColor = getUserTypeColor(currentUserType);

  const [currentHighlightId, setCurrentHighlightId] = useState(null);

  // Infinite scroll pagination state (using refs to avoid stale closures)
  const currentPageRef = useRef(1);
  const hasMoreRef = useRef(true);
  const isLoadingMoreRef = useRef(false);
  const [hasMore, setHasMore] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const loadMoreRef = useRef(null);

  // Load comments (initial or paginated)
  const loadComments = useCallback(async (page = 1, append = false) => {
    if (!post.id) return;
    if (append && isLoadingMoreRef.current) return;

    if (append) {
      isLoadingMoreRef.current = true;
      setIsLoadingMore(true);
    }

    try {
      const response = await commentsAPI.getPostComments(post.id, {
        page,
        perPage: 20
      });

      if (response.data && response.data.data) {
        const transformedComments = transformCommentList(response.data.data);
        const pagination = response.data;
        const moreAvailable = pagination.current_page < pagination.last_page;

        if (append) {
          setComments(prev => [...prev, ...transformedComments]);
        } else {
          setComments(transformedComments);
        }

        hasMoreRef.current = moreAvailable;
        setHasMore(moreAvailable);
      }
    } catch (error) {
      console.error('Error loading comments:', error);
    } finally {
      if (append) {
        isLoadingMoreRef.current = false;
        setIsLoadingMore(false);
      }
    }
  }, [post.id]);

  // Initial load when post changes
  useEffect(() => {
    if (post.id) {
      currentPageRef.current = 1;
      hasMoreRef.current = true;
      isLoadingMoreRef.current = false;
      setHasMore(true);
      loadComments(1, false);
    }
  }, [post.id, loadComments]);

  // Infinite scroll: load more when sentinel enters the scrollable area
  useEffect(() => {
    const sentinel = loadMoreRef.current;
    if (!sentinel) return;

    // Find the nearest scrollable parent to use as the observer root
    const getScrollableParent = (el) => {
      let parent = el.parentElement;
      while (parent) {
        const style = window.getComputedStyle(parent);
        if (/(auto|scroll)/.test(style.overflowY)) {
          return parent;
        }
        parent = parent.parentElement;
      }
      return null;
    };

    const scrollRoot = getScrollableParent(sentinel);

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMoreRef.current && !isLoadingMoreRef.current) {
          const nextPage = currentPageRef.current + 1;
          currentPageRef.current = nextPage;
          loadComments(nextPage, true);
        }
      },
      { root: scrollRoot, rootMargin: '200px', threshold: 0 }
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [post.id, loadComments]);

  // Handle comment highlighting and scrolling
  useEffect(() => {
    if (highlightCommentId && comments.length > 0) {
      setCurrentHighlightId(highlightCommentId);

      // Scroll to the comment after a short delay
      const timer = setTimeout(() => {
        const element = document.getElementById(`comment-${highlightCommentId}`);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 500);

      // Clear highlight after 5 seconds
      const clearTimer = setTimeout(() => {
        setCurrentHighlightId(null);
      }, 5000);

      return () => {
        clearTimeout(timer);
        clearTimeout(clearTimer);
      };
    }
  }, [highlightCommentId, comments]);

  const handleSubmitComment = async (e) => {
    e.preventDefault();

    if (!commentText.trim()) return;

    setIsSubmitting(true);

    try {
      // Use existing comments API
      const response = await commentsAPI.createComment(post.id, {
        text: commentText
      });

      // Handle different response structures
      const responseData = response.data?.data || response.data || response;

      // Get current user data as fallback - ensure proper URL formatting
      const currentUserName = currentUser
        ? `${currentUser.first_name || ''} ${currentUser.last_name || ''}`.trim() || currentUser.username || currentUser.name || 'User'
        : 'User';

      // Get profile image URL with proper formatting
      let currentUserImage = getUserPlaceholderImage(currentUser); // Default fallback
      if (currentUser) {
        const profileImageUrl = getProfileImageUrl();
        if (profileImageUrl) {
          currentUserImage = profileImageUrl;
        } else {
          // Try to format the profile_image_url from user object
          const rawImageUrl = currentUser.profile_image_url || currentUser.profileImageUrl || currentUser.avatar || currentUser.profile_image;
          if (rawImageUrl) {
            const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000';
            if (rawImageUrl.startsWith('/storage/') || rawImageUrl.startsWith('/uploads/')) {
              currentUserImage = `${API_BASE_URL}${rawImageUrl}`;
            } else if (rawImageUrl.startsWith('http')) {
              currentUserImage = rawImageUrl;
            } else {
              currentUserImage = `${API_BASE_URL}/${rawImageUrl}`;
            }
          }
        }
      }

      if (responseData) {
        const transformedComment = socialUtils.transformCommentData(responseData);

        if (transformedComment && transformedComment.user) {
          // Format the avatar URL if it exists
          let avatarUrl = transformedComment.user.avatar;
          if (avatarUrl) {
            // If it's already a full URL, use it as-is
            if (avatarUrl.startsWith('http')) {
              // Already a full URL, use as-is
            } else if (avatarUrl.startsWith('/storage/') || avatarUrl.startsWith('/uploads/')) {
              // Relative path starting with /storage or /uploads
              const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000';
              avatarUrl = `${API_BASE_URL}${avatarUrl}`;
            } else if (!avatarUrl.startsWith('/')) {
              // Relative path without leading slash
              const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000';
              avatarUrl = `${API_BASE_URL}/${avatarUrl}`;
            }
            // If it starts with / but not /storage or /uploads, it's likely a local path like /icons/person.png
          } else {
            avatarUrl = currentUserImage;
          }

          const newComment = {
            id: transformedComment.id,
            content: transformedComment.text,
            author: {
              id: currentUser?.id || currentUser?.user_id,
              name: transformedComment.user.name || currentUserName,
              username: transformedComment.user.name || currentUserName,
              profileImage: avatarUrl || currentUserImage
            },
            user_id: currentUser?.id || currentUser?.user_id,
            createdAt: transformedComment.timestamp || "Just now",
            likes: transformedComment.total_reactions || 0,
            user_reaction_type: transformedComment.user_reaction_type || null,
            reaction_counts: transformedComment.reaction_counts || {},
            total_reactions: transformedComment.total_reactions || 0
          };

          setComments(prev => [newComment, ...prev]);
          setCommentText("");
          onCommentAdded(newComment);
        } else {
          // If transformation fails or user data is missing, create comment with current user data
          const newComment = {
            id: responseData.id || Date.now(),
            content: commentText,
            author: {
              name: currentUserName,
              username: currentUserName,
              profileImage: currentUserImage
            },
            createdAt: "Just now",
            likes: 0,
            user_reaction_type: null,
            reaction_counts: {},
            total_reactions: 0
          };

          setComments(prev => [newComment, ...prev]);
          setCommentText("");
          onCommentAdded(newComment);
        }
      }
    } catch (error) {
      console.error("Error adding comment:", error);
      setErrorModal({
        isOpen: true,
        message: t(
          "channelPostComments.addError",
          "Failed to add comment. Please try again."
        )
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const isArabicText = (text) => {
    if (!text || text.trim() === "") return false;
    const firstChar = text.trim().charAt(0);
    const arabicRegex = /[\u0600-\u06FF\u0750-\u077F\u08A0-\u08FF\uFB50-\uFDFF\uFE70-\uFEFF]/;
    return arabicRegex.test(firstChar);
  };

  const formatContent = (content) => {
    if (!content) return "";

    const isArabic = isArabicText(content);

    // Robustly linkify URLs
    const urlRegex = /(<a\b[^>]*>[\s\S]*?<\/a>)|(<[^>]+>)|(https?:\/\/[^\s<]+)/gi;
    const linkifiedContent = content.replace(urlRegex, (match, aTag, otherTag, url) => {
      if (aTag) return aTag;
      if (otherTag) return otherTag;
      if (url) {
        const displayUrl = socialUtils.truncateUrl(url);
        return `<a href="${url}" target="_blank" rel="noopener noreferrer" style="color: #00D4B3; text-decoration: underline; word-break: break-all;" onClick="event.stopPropagation()">${displayUrl}</a>`;
      }
      return match;
    });

    // Check if originally the content had HTML tags
    const hasHTML = /<[^>]+>/.test(content);

    if (hasHTML) {
      return (
        <div
          style={{
            direction: isArabic ? "rtl" : "ltr",
            textAlign: isArabic ? "right" : "left",
            lineHeight: "1.5",
          }}
          dangerouslySetInnerHTML={{ __html: linkifiedContent }}
        />
      );
    }

    return linkifiedContent.split("\n").map((line, index) => {
      if (line.trim().startsWith("•")) {
        return (
          <div
            key={index}
            style={{
              direction: isArabic ? "rtl" : "ltr",
              textAlign: isArabic ? "right" : "left",
            }}
          >
            <span dangerouslySetInnerHTML={{ __html: line.trim() }} />
          </div>
        );
      }
      return (
        <div
          key={index}
          style={{
            direction: isArabic ? "rtl" : "ltr",
            textAlign: isArabic ? "right" : "left",
            marginBottom: line.trim() ? "4px" : "2px",
          }}
        >
          <span dangerouslySetInnerHTML={{ __html: line || "\u00A0" }} />
        </div>
      );
    });
  };

  // Custom emoji image paths
  const HappyEmoji = "/icons/Reactions/Sulhafa_Emoji_Happy.png";
  const LoveEmoji = "/icons/Reactions/Sulhafa_Emoji_Love.png";
  const LaughEmoji = "/icons/Reactions/Sulhafa_Emoji_Laugh.png";
  const ShockedEmoji = "/icons/Reactions/Sulhafa_Emoji_Shocked.png";
  const SadEmoji = "/icons/Reactions/Sulhafa_Emoji_Sad.png";
  const AngryEmoji = "/icons/Reactions/Sulhafa_Emoji_Angry.png";

  // Reaction types
  const reactions = [HappyEmoji, LoveEmoji, LaughEmoji, ShockedEmoji, SadEmoji, AngryEmoji];
  const reactionTypes = ['like', 'love', 'funny', 'wow', 'sad', 'angry'];
  const reactionTypeToEmoji = {
    'like': HappyEmoji,
    'love': LoveEmoji,
    'funny': LaughEmoji,
    'haha': LaughEmoji,
    'laugh': LaughEmoji,
    'wow': ShockedEmoji,
    'sad': SadEmoji,
    'angry': AngryEmoji
  };
  const emojiToReactionType = {
    [HappyEmoji]: 'like',
    [LoveEmoji]: 'love',
    [LaughEmoji]: 'funny',
    [ShockedEmoji]: 'wow',
    [SadEmoji]: 'sad',
    [AngryEmoji]: 'angry'
  };

  // Helper function to render reaction breakdown
  const renderReactionBreakdown = (reactionCounts) => {
    if (!reactionCounts || typeof reactionCounts !== 'object' || Object.keys(reactionCounts).length === 0) {
      return null;
    }

    // Filter out reactions with 0 count and sort by count (descending)
    const activeReactions = Object.entries(reactionCounts)
      .filter(([type, count]) => count > 0)
      .sort(([, a], [, b]) => b - a);

    if (activeReactions.length === 0) {
      return null;
    }

    return (
      <div className="channel-post-comment-reaction-breakdown">
        <div style={{ display: "flex", alignItems: "center" }}>
          {activeReactions.map(([type, count], index) => {
            const emoji = reactionTypeToEmoji[type] || HappyEmoji;
            const isImagePath = typeof emoji === 'string' && (emoji.startsWith('/') || emoji.includes('.png') || emoji.includes('.jpg') || emoji.includes('.svg'));
            return (
              <span
                key={type}
                className="channel-post-comment-reaction-item"
                title={`${count} ${type} reaction${count !== 1 ? 's' : ''}`}
                style={{ zIndex: 10 - index }}
              >
                <span className="channel-post-comment-reaction-emoji">
                  {isImagePath ? (
                    <img
                      src={emoji}
                      alt={type}
                      onError={(e) => {
                        console.error('Failed to load reaction emoji:', emoji);
                        e.target.style.display = 'none';
                      }}
                      style={{ width: "100%", height: "100%", objectFit: 'contain' }}
                    />
                  ) : (
                    <span>{emoji}</span>
                  )}
                </span>
                <span className="channel-post-comment-reaction-count">{count}</span>
              </span>
            );
          })}
        </div>
        <span className="channel-post-comment-reaction-count">
          {Object.values(reactionCounts).reduce((sum, count) => sum + (count || 0), 0)}
        </span>
      </div>
    );
  };

  // Clear timeout helper
  const clearReactionTimeout = (commentId) => {
    if (timeoutRefs.current[commentId]) {
      window.clearTimeout(timeoutRefs.current[commentId]);
      timeoutRefs.current[commentId] = null;
    }
  };

  // Handle mouse enter for reaction picker
  const handleMouseEnter = (commentId) => {
    clearReactionTimeout(commentId);
    setShowReactionPicker(prev => ({ ...prev, [commentId]: true }));
  };

  // Handle mouse leave for reaction picker
  const handleMouseLeave = (commentId, e) => {
    // Don't close if moving to the picker
    const relatedTarget = e?.relatedTarget;
    const container = containerRefs.current[commentId];
    if (relatedTarget && container && container.contains(relatedTarget)) {
      return;
    }
    clearReactionTimeout(commentId);
    timeoutRefs.current[commentId] = window.setTimeout(() => {
      setShowReactionPicker(prev => ({ ...prev, [commentId]: false }));
    }, 500); // Increased delay to give more time to move to picker
  };

  // Handle comment reaction
  const handleCommentReaction = async (commentId, reaction, event) => {
    // Prevent the click from bubbling up
    if (event) {
      event.stopPropagation();
      event.preventDefault();
    }

    if (reactingComments[commentId]) return;

    // Close picker immediately to prevent conflicts
    setShowReactionPicker(prev => ({ ...prev, [commentId]: false }));
    clearReactionTimeout(commentId);

    setReactingComments(prev => ({ ...prev, [commentId]: true }));

    try {
      const comment = comments.find(c => c.id === commentId);
      if (!comment) return;

      const reactionType = emojiToReactionType[reaction] || 'like';
      const currentReaction = comment.user_reaction_type;

      if (currentReaction === reactionType) {
        // Optimistic update: remove reaction immediately
        setComments(prev => prev.map(c => {
          if (c.id !== commentId) return c;
          const currentCounts = { ...(c.reaction_counts || {}) };
          return {
            ...c,
            user_reaction_type: null,
            total_reactions: Math.max(0, (c.total_reactions || 0) - 1),
            likes: Math.max(0, (c.likes || 0) - 1),
            reaction_counts: {
              ...currentCounts,
              [reactionType]: Math.max(0, (currentCounts[reactionType] || 0) - 1)
            }
          };
        }));

        // Remove reaction
        const response = await likesAPI.removeCommentReaction(commentId);

        // Update with server response if available
        if (response && response.data) {
          const responseData = response.data;
          setComments(prev => prev.map(c =>
            c.id === commentId
              ? {
                ...c,
                user_reaction_type: null,
                total_reactions: responseData.reactions_count !== undefined
                  ? responseData.reactions_count
                  : Math.max(0, (c.total_reactions || 0) - 1),
                likes: responseData.reactions_count !== undefined
                  ? responseData.reactions_count
                  : Math.max(0, (c.likes || 0) - 1),
                reaction_counts: responseData.reaction_counts || c.reaction_counts
              }
              : c
          ));
        }
      } else {
        // Add/change reaction
        let response;
        try {
          response = await likesAPI.addCommentReaction(commentId, reactionType);
        } catch (apiError) {
          // Check if error is from notification/broadcasting (non-critical)
          const errorMessage = apiError?.response?.data?.message || apiError?.message || '';
          const errorTrace = JSON.stringify(apiError?.response?.data?.trace || []);
          const isNotificationError = errorMessage.includes('message') ||
            errorMessage.includes('Pusher') ||
            errorMessage.includes('notification') ||
            errorMessage.includes('BroadcastException') ||
            errorTrace.includes('NotificationService') ||
            errorTrace.includes('PusherBroadcaster');

          // Check if error response contains reaction data (reaction was saved despite error)
          const hasReactionData = apiError?.response?.data?.reaction ||
            apiError?.response?.data?.reaction_counts ||
            apiError?.response?.data?.reactions_count !== undefined;

          // If it's a notification/broadcasting error (even 500), the reaction was likely saved
          if (isNotificationError || (apiError?.response?.status === 500 && hasReactionData)) {
            // Use the error response data if it contains reaction info (reaction was saved)
            if (hasReactionData) {
              response = apiError.response; // Reaction was saved, use the data
            } else {
              // No data in error, do optimistic update
              response = null; // Will trigger optimistic update below
            }
            // Silently handle - don't log since reaction worked
          } else if (apiError?.response?.status === 200 || apiError?.response?.status === 201) {
            response = apiError.response; // Use the response even if there were errors
          } else {
            throw apiError; // Re-throw if it's a different error
          }
        }

        // Optimistic update: add/change reaction immediately
        const oldReaction = comment.user_reaction_type;
        const isSwitching = oldReaction && oldReaction !== reactionType;
        const isAdding = !oldReaction;

        setComments(prev => prev.map(c => {
          if (c.id !== commentId) return c;
          const currentCounts = { ...(c.reaction_counts || {}) };
          const updatedCounts = { ...currentCounts };

          // If switching from one reaction to another:
          // 1. Decrement the old reaction count
          // 2. Increment the new reaction count
          // Total reactions count stays the same (switching, not adding)
          if (isSwitching) {
            updatedCounts[oldReaction] = Math.max(0, (updatedCounts[oldReaction] || 0) - 1);
            updatedCounts[reactionType] = (updatedCounts[reactionType] || 0) + 1;
          } else if (isAdding) {
            updatedCounts[reactionType] = (updatedCounts[reactionType] || 0) + 1;
          }

          const currentTotal = c.total_reactions || c.likes || 0;

          return {
            ...c,
            user_reaction_type: reactionType,
            total_reactions: isSwitching ? currentTotal : (isAdding ? currentTotal + 1 : currentTotal),
            likes: isSwitching ? currentTotal : (isAdding ? currentTotal + 1 : currentTotal),
            reaction_counts: updatedCounts
          };
        }));

        // Update with server response if available
        if (response && response.data) {
          const responseData = response.data;
          setComments(prev => prev.map(c => {
            if (c.id !== commentId) return c;

            // Use backend's reaction_counts directly - it should have correct counts
            // (old reaction removed, new reaction added when switching)
            const backendReactionCounts = responseData.reaction_counts;

            // If backend provides reaction_counts, use it; otherwise keep optimistic update
            const finalReactionCounts = backendReactionCounts && Object.keys(backendReactionCounts).length > 0
              ? backendReactionCounts
              : c.reaction_counts; // Keep the optimistic update

            return {
              ...c,
              user_reaction_type: responseData.reaction?.type || reactionType,
              total_reactions: responseData.reactions_count !== undefined
                ? responseData.reactions_count
                : c.total_reactions, // Keep optimistic value if backend doesn't provide
              likes: responseData.reactions_count !== undefined
                ? responseData.reactions_count
                : c.likes, // Keep optimistic value if backend doesn't provide
              reaction_counts: finalReactionCounts
            };
          }));
        }
      }
    } catch (error) {
      // Check if error is from notification/broadcasting (non-critical)
      const errorMessage = error?.response?.data?.message || error?.message || '';
      const errorTrace = JSON.stringify(error?.response?.data?.trace || []);
      const isNotificationError = errorMessage.includes('message') ||
        errorMessage.includes('Pusher') ||
        errorMessage.includes('notification') ||
        errorMessage.includes('BroadcastException') ||
        errorTrace.includes('NotificationService') ||
        errorTrace.includes('PusherBroadcaster');

      // Even if there's an error (like notification service issue),
      // try to update the UI optimistically - reaction was likely saved
      const reactionType = emojiToReactionType[reaction] || 'like';
      const comment = comments.find(c => c.id === commentId);
      if (comment) {
        const currentReaction = comment.user_reaction_type;

        if (currentReaction === reactionType) {
          // Optimistic update for removal
          setComments(prev => prev.map(c =>
            c.id === commentId
              ? {
                ...c,
                user_reaction_type: null,
                total_reactions: Math.max(0, (c.total_reactions || 0) - 1),
                likes: Math.max(0, (c.likes || 0) - 1),
                reaction_counts: {
                  ...c.reaction_counts,
                  [reactionType]: Math.max(0, (c.reaction_counts[reactionType] || 0) - 1)
                }
              }
              : c
          ));
        } else {
          // Optimistic update for addition
          const oldReaction = comment.user_reaction_type;
          const newCounts = { ...comment.reaction_counts };
          if (oldReaction) {
            newCounts[oldReaction] = Math.max(0, (newCounts[oldReaction] || 0) - 1);
          }
          newCounts[reactionType] = (newCounts[reactionType] || 0) + 1;

          setComments(prev => prev.map(c => {
            if (c.id !== commentId) return c;
            return {
              ...c,
              user_reaction_type: reactionType,
              total_reactions: (c.total_reactions || 0) + (oldReaction ? 0 : 1),
              likes: (c.likes || 0) + (oldReaction ? 0 : 1),
              reaction_counts: newCounts
            };
          }));
        }
      }

      // Only log unexpected errors - notification errors are expected and reaction worked
      if (!isNotificationError) {
        console.error("Error reacting to comment:", error);
      }
      // Silently handle notification errors since reaction was saved successfully
    } finally {
      setReactingComments(prev => ({ ...prev, [commentId]: false }));
    }
  };

  // Load replies for a comment
  const loadReplies = async (commentId) => {
    if (loadingReplies[commentId]) return;

    setLoadingReplies(prev => ({ ...prev, [commentId]: true }));

    try {
      const response = await commentsAPI.getCommentReplies(post.id, commentId, {
        page: 1,
        perPage: 10
      });

      const repliesData = response.data?.data || response.data || [];
      const transformedReplies = repliesData
        .map(socialUtils.transformCommentData)
        .filter(reply => reply !== null)
        .map(reply => ({
          id: reply.id,
          content: reply.text,
          author: {
            id: reply.user?.id || reply.user?.user_id,
            name: reply.user.name,
            username: reply.user.name,
            profileImage: reply.user.avatar
          },
          user_id: reply.user?.id || reply.user?.user_id,
          createdAt: reply.timestamp,
          likes: reply.total_reactions || 0,
          user_reaction_type: reply.user_reaction_type || null,
          reaction_counts: reply.reaction_counts || {},
          total_reactions: reply.total_reactions || 0,
          parent_id: commentId
        }));

      setReplies(prev => ({ ...prev, [commentId]: transformedReplies }));
    } catch (error) {
      console.error('Error loading replies:', error);
      setReplies(prev => ({ ...prev, [commentId]: [] }));
    } finally {
      setLoadingReplies(prev => ({ ...prev, [commentId]: false }));
    }
  };


  // Toggle reply input visibility
  const toggleReplyInput = (commentId) => {
    setShowReplyInput(prev => ({ ...prev, [commentId]: !prev[commentId] }));
    if (!showReplyInput[commentId]) {
      setReplyInputs(prev => ({ ...prev, [commentId]: '' }));
    }
  };

  // Toggle replies visibility
  const toggleReplies = (commentId) => {
    if (!showReplies[commentId] && !replies[commentId]) {
      loadReplies(commentId);
    }
    const newShowReplies = !showReplies[commentId];
    setShowReplies(prev => ({ ...prev, [commentId]: newShowReplies }));
    // Automatically show reply input when showing replies
    if (newShowReplies) {
      setShowReplyInput(prev => ({ ...prev, [commentId]: true }));
    }
  };

  // Clear timeout helper for replies
  const clearReplyReactionTimeout = (replyId) => {
    if (replyTimeoutRefs.current[replyId]) {
      window.clearTimeout(replyTimeoutRefs.current[replyId]);
      replyTimeoutRefs.current[replyId] = null;
    }
  };

  // Handle mouse enter for reply reaction picker
  const handleReplyMouseEnter = (replyId) => {
    clearReplyReactionTimeout(replyId);
    setShowReplyReactionPicker(prev => ({ ...prev, [replyId]: true }));
  };

  // Handle mouse leave for reply reaction picker
  const handleReplyMouseLeave = (replyId, e) => {
    const relatedTarget = e?.relatedTarget;
    const container = replyContainerRefs.current[replyId];
    if (relatedTarget && container && container.contains(relatedTarget)) {
      return;
    }
    clearReplyReactionTimeout(replyId);
    replyTimeoutRefs.current[replyId] = window.setTimeout(() => {
      setShowReplyReactionPicker(prev => ({ ...prev, [replyId]: false }));
    }, 500);
  };

  // Handle reply reaction
  const handleReplyReaction = async (replyId, reaction, event, commentId) => {
    if (event) {
      event.stopPropagation();
      event.preventDefault();
    }

    if (reactingReplies[replyId]) return;

    setShowReplyReactionPicker(prev => ({ ...prev, [replyId]: false }));
    clearReplyReactionTimeout(replyId);

    setReactingReplies(prev => ({ ...prev, [replyId]: true }));

    try {
      const reply = replies[commentId]?.find(r => r.id === replyId);
      if (!reply) return;

      const reactionType = emojiToReactionType[reaction] || 'like';
      const currentReaction = reply.user_reaction_type;

      if (currentReaction === reactionType) {
        // Remove reaction
        setReplies(prev => ({
          ...prev,
          [commentId]: prev[commentId].map(r => {
            if (r.id !== replyId) return r;
            const currentCounts = { ...(r.reaction_counts || {}) };
            return {
              ...r,
              user_reaction_type: null,
              total_reactions: Math.max(0, (r.total_reactions || 0) - 1),
              likes: Math.max(0, (r.likes || 0) - 1),
              reaction_counts: {
                ...currentCounts,
                [reactionType]: Math.max(0, (currentCounts[reactionType] || 0) - 1)
              }
            };
          })
        }));

        const response = await likesAPI.removeCommentReaction(replyId);

        if (response && response.data) {
          const responseData = response.data;
          setReplies(prev => ({
            ...prev,
            [commentId]: prev[commentId].map(r =>
              r.id === replyId
                ? {
                  ...r,
                  user_reaction_type: null,
                  total_reactions: responseData.reactions_count !== undefined
                    ? responseData.reactions_count
                    : Math.max(0, (r.total_reactions || 0) - 1),
                  likes: responseData.reactions_count !== undefined
                    ? responseData.reactions_count
                    : Math.max(0, (r.likes || 0) - 1),
                  reaction_counts: responseData.reaction_counts || r.reaction_counts
                }
                : r
            )
          }));
        }
      } else {
        // Add/change reaction
        let response;
        try {
          response = await likesAPI.addCommentReaction(replyId, reactionType);
        } catch (apiError) {
          const errorMessage = apiError?.response?.data?.message || apiError?.message || '';
          const errorTrace = JSON.stringify(apiError?.response?.data?.trace || []);
          const isNotificationError = errorMessage.includes('message') ||
            errorMessage.includes('Pusher') ||
            errorMessage.includes('notification') ||
            errorMessage.includes('BroadcastException') ||
            errorTrace.includes('NotificationService') ||
            errorTrace.includes('PusherBroadcaster');

          const hasReactionData = apiError?.response?.data?.reaction ||
            apiError?.response?.data?.reaction_counts ||
            apiError?.response?.data?.reactions_count !== undefined;

          if (isNotificationError || (apiError?.response?.status === 500 && hasReactionData)) {
            if (hasReactionData) {
              response = apiError.response;
            } else {
              response = null;
            }
          } else if (apiError?.response?.status === 200 || apiError?.response?.status === 201) {
            response = apiError.response;
          } else {
            throw apiError;
          }
        }

        // Optimistic update
        const oldReaction = reply.user_reaction_type;
        const isSwitching = oldReaction && oldReaction !== reactionType;
        const isAdding = !oldReaction;

        setReplies(prev => ({
          ...prev,
          [commentId]: prev[commentId].map(r => {
            if (r.id !== replyId) return r;
            const currentCounts = { ...(r.reaction_counts || {}) };
            const updatedCounts = { ...currentCounts };

            if (isSwitching) {
              updatedCounts[oldReaction] = Math.max(0, (updatedCounts[oldReaction] || 0) - 1);
              updatedCounts[reactionType] = (updatedCounts[reactionType] || 0) + 1;
            } else if (isAdding) {
              updatedCounts[reactionType] = (updatedCounts[reactionType] || 0) + 1;
            }

            const currentTotal = r.total_reactions || r.likes || 0;

            return {
              ...r,
              user_reaction_type: reactionType,
              total_reactions: isSwitching ? currentTotal : (isAdding ? currentTotal + 1 : currentTotal),
              likes: isSwitching ? currentTotal : (isAdding ? currentTotal + 1 : currentTotal),
              reaction_counts: updatedCounts
            };
          })
        }));

        if (response && response.data) {
          const responseData = response.data;
          setReplies(prev => ({
            ...prev,
            [commentId]: prev[commentId].map(r => {
              if (r.id !== replyId) return r;

              const backendReactionCounts = responseData.reaction_counts;
              const finalReactionCounts = backendReactionCounts && Object.keys(backendReactionCounts).length > 0
                ? backendReactionCounts
                : r.reaction_counts;

              return {
                ...r,
                user_reaction_type: responseData.reaction?.type || reactionType,
                total_reactions: responseData.reactions_count !== undefined
                  ? responseData.reactions_count
                  : r.total_reactions,
                likes: responseData.reactions_count !== undefined
                  ? responseData.reactions_count
                  : r.likes,
                reaction_counts: finalReactionCounts
              };
            })
          }));
        }
      }
    } catch (error) {
      const errorMessage = error?.response?.data?.message || error?.message || '';
      const errorTrace = JSON.stringify(error?.response?.data?.trace || []);
      const isNotificationError = errorMessage.includes('message') ||
        errorMessage.includes('Pusher') ||
        errorMessage.includes('notification') ||
        errorMessage.includes('BroadcastException') ||
        errorTrace.includes('NotificationService') ||
        errorTrace.includes('PusherBroadcaster');

      if (!isNotificationError) {
        console.error("Error reacting to reply:", error);
      }
    } finally {
      setReactingReplies(prev => ({ ...prev, [replyId]: false }));
    }
  };

  // Check if current user owns a comment/reply
  const isCommentOwner = (comment) => {
    if (!currentUser) return false;
    const currentUserId = currentUser.id || currentUser.user_id;
    // Check various possible locations for the comment author's user ID
    // The comment might have author.id, user_id, or user.id depending on transformation
    const commentUserId = comment.author?.id ||
      comment.user_id ||
      comment.user?.id ||
      comment.author?.user_id;
    return currentUserId === commentUserId;
  };

  // Handle comment deletion
  const handleDeleteCommentClick = (commentId) => {
    setDeleteCommentModal({ commentId, isOpen: true });
  };

  const handleDeleteCommentConfirm = async () => {
    const commentId = deleteCommentModal.commentId;
    if (!commentId) return;

    setDeleteCommentModal({ commentId: null, isOpen: false });
    setDeletingComments(prev => ({ ...prev, [commentId]: true }));

    try {
      await commentsAPI.deleteComment(post.id, commentId);

      // Remove comment from state
      setComments(prev => prev.filter(c => c.id !== commentId));

      // Update comment count if callback provided
      if (onCommentAdded) {
        // This is a bit of a hack - we'll use the callback to notify parent
        // that a comment was removed (negative increment)
      }
    } catch (error) {
      console.error("Error deleting comment:", error);
      setErrorModal({
        isOpen: true,
        message: t(
          "channelPostComments.deleteError",
          "Failed to delete comment. Please try again."
        )
      });
    } finally {
      setDeletingComments(prev => ({ ...prev, [commentId]: false }));
    }
  };

  const handleDeleteCommentCancel = () => {
    setDeleteCommentModal({ commentId: null, isOpen: false });
  };

  // Handle reply deletion
  const handleDeleteReplyClick = (replyId, commentId) => {
    setDeleteReplyModal({ replyId, commentId, isOpen: true });
  };

  const handleDeleteReplyConfirm = async () => {
    const { replyId, commentId } = deleteReplyModal;
    if (!replyId || !commentId) return;

    setDeleteReplyModal({ replyId: null, commentId: null, isOpen: false });
    setDeletingReplies(prev => ({ ...prev, [replyId]: true }));

    try {
      await commentsAPI.deleteComment(post.id, replyId);

      // Remove reply from state
      setReplies(prev => ({
        ...prev,
        [commentId]: (prev[commentId] || []).filter(r => r.id !== replyId)
      }));

      // Update parent comment's replies_count
      setComments(prev => prev.map(c =>
        c.id === commentId
          ? { ...c, replies_count: Math.max(0, (c.replies_count || 0) - 1) }
          : c
      ));
    } catch (error) {
      console.error("Error deleting reply:", error);
      setErrorModal({
        isOpen: true,
        message: t(
          "channelPostComments.deleteError",
          "Failed to delete reply. Please try again."
        )
      });
    } finally {
      setDeletingReplies(prev => ({ ...prev, [replyId]: false }));
    }
  };

  const handleDeleteReplyCancel = () => {
    setDeleteReplyModal({ replyId: null, commentId: null, isOpen: false });
  };

  // Handle reply submission
  const handleReplySubmit = async (commentId, e) => {
    e.preventDefault();

    const replyText = replyInputs[commentId]?.trim();
    if (!replyText) return;

    setSubmittingReplies(prev => ({ ...prev, [commentId]: true }));

    try {
      const response = await commentsAPI.createReply(post.id, commentId, {
        text: replyText
      });

      const responseData = response.data?.data || response.data || response;

      // Get current user data
      const currentUserName = currentUser
        ? `${currentUser.first_name || ''} ${currentUser.last_name || ''}`.trim() || currentUser.username || currentUser.name || 'User'
        : 'User';

      let currentUserImage = getUserPlaceholderImage(currentUser);
      if (currentUser) {
        const profileImageUrl = getProfileImageUrl();
        if (profileImageUrl) {
          currentUserImage = profileImageUrl;
        } else {
          const rawImageUrl = currentUser.profile_image_url || currentUser.profileImageUrl || currentUser.avatar || currentUser.profile_image;
          if (rawImageUrl) {
            const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000';
            if (rawImageUrl.startsWith('/storage/') || rawImageUrl.startsWith('/uploads/')) {
              currentUserImage = `${API_BASE_URL}${rawImageUrl}`;
            } else if (rawImageUrl.startsWith('http')) {
              currentUserImage = rawImageUrl;
            } else {
              currentUserImage = `${API_BASE_URL}/${rawImageUrl}`;
            }
          }
        }
      }

      if (responseData) {
        const transformedReply = socialUtils.transformCommentData(responseData);

        const newReply = {
          id: transformedReply?.id || responseData.id || Date.now(),
          content: transformedReply?.text || responseData.text || replyText,
          author: {
            id: currentUser?.id || currentUser?.user_id,
            name: transformedReply?.user?.name || currentUserName,
            username: transformedReply?.user?.name || currentUserName,
            profileImage: transformedReply?.user?.avatar || currentUserImage
          },
          user_id: currentUser?.id || currentUser?.user_id,
          createdAt: transformedReply?.timestamp || "Just now",
          likes: transformedReply?.total_reactions || 0,
          user_reaction_type: transformedReply?.user_reaction_type || null,
          reaction_counts: transformedReply?.reaction_counts || {},
          total_reactions: transformedReply?.total_reactions || 0,
          parent_id: commentId
        };

        setReplies(prev => ({
          ...prev,
          [commentId]: [newReply, ...(prev[commentId] || [])]
        }));

        // Update comment's replies_count
        setComments(prev => prev.map(c =>
          c.id === commentId
            ? { ...c, replies_count: (c.replies_count || 0) + 1 }
            : c
        ));

        setReplyInputs(prev => ({ ...prev, [commentId]: '' }));
      }
    } catch (error) {
      console.error("Error adding reply:", error);
      setErrorModal({
        isOpen: true,
        message: t(
          "channelPostComments.replyError",
          "Failed to add reply. Please try again."
        )
      });
    } finally {
      setSubmittingReplies(prev => ({ ...prev, [commentId]: false }));
    }
  };

  // Close reaction picker when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Handle comment reaction pickers
      Object.keys(containerRefs.current).forEach(commentId => {
        const container = containerRefs.current[commentId];
        if (container && !container.contains(event.target)) {
          setShowReactionPicker(prev => ({ ...prev, [commentId]: false }));
          clearReactionTimeout(commentId);
        }
      });

      // Handle reply reaction pickers
      Object.keys(replyContainerRefs.current).forEach(replyId => {
        const container = replyContainerRefs.current[replyId];
        if (container && !container.contains(event.target)) {
          setShowReplyReactionPicker(prev => ({ ...prev, [replyId]: false }));
          clearReplyReactionTimeout(replyId);
        }
      });
    };

    document.addEventListener('click', handleClickOutside, true);
    return () => {
      document.removeEventListener('click', handleClickOutside, true);
    };
  }, []);

  return (
    <div className="channel-post-comments" style={{ '--user-type-color': userTypeColor }}>
      <div className="channel-post-comments-list">
        {comments.length > 0 ? (
          <>
            {comments.map(comment => (
              <div
                key={comment.id}
                id={`comment-${comment.id}`}
                className={`channel-post-comment ${currentHighlightId === comment.id ? 'channel-post-comment-highlighted' : ''}`}
              >
              <img
                src={comment.author.profileImage || getUserPlaceholderImage(comment.author)}
                alt={comment.author.name}
                className="channel-post-comment-avatar"
                onError={(e) => {
                  e.target.src = getUserPlaceholderImage(comment.author);
                }}
              />
              <div className="channel-post-comment-content">
                <div className="channel-post-comment-header">
                  <span className="channel-post-comment-author">{comment.author.name}</span>
                  <span className="channel-post-comment-time">{comment.createdAt}</span>
                </div>
                <div
                  className="channel-post-comment-text"
                  dir={isArabicText(comment.content) ? 'rtl' : 'ltr'}
                >
                  {formatContent(comment.content)}
                </div>
                <div className="channel-post-comment-actions">
                  <div className="channel-post-comment-actions-wrapper">
                    <div
                      ref={(el) => containerRefs.current[comment.id] = el}
                      className="channel-post-comment-reaction-container"
                      onMouseEnter={() => handleMouseEnter(comment.id)}
                      onMouseLeave={(e) => handleMouseLeave(comment.id, e)}
                      style={{ position: 'relative' }}
                    >
                      <button
                        className={`channel-post-comment-like-btn ${comment.user_reaction_type ? 'has-reaction' : ''}`}
                        onClick={(e) => {
                          e.preventDefault();
                          if (!showReactionPicker[comment.id]) {
                            handleMouseEnter(comment.id);
                          }
                        }}
                      >
                        {comment.user_reaction_type && reactionTypeToEmoji[comment.user_reaction_type] ? (
                          (() => {
                            const emoji = reactionTypeToEmoji[comment.user_reaction_type];
                            const isImagePath = typeof emoji === 'string' && (emoji.startsWith('/') || emoji.includes('.png') || emoji.includes('.jpg') || emoji.includes('.svg'));
                            return isImagePath ? (
                              <img
                                src={emoji}
                                alt={comment.user_reaction_type}
                                style={{ width: "16px", height: "16px", objectFit: 'contain', display: 'inline-block', transform: 'scale(1.5)' }}
                              />
                            ) : (
                              <span style={{ fontSize: '16px' }}>{emoji}</span>
                            );
                          })()
                        ) : (
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"></path>
                          </svg>
                        )}
                        <span>{comment.total_reactions || comment.likes || 0}</span>
                      </button>

                      {showReactionPicker[comment.id] && (
                        <div
                          className="channel-post-reaction-picker"
                          style={{ bottom: '100%', marginBottom: '8px' }}
                          onMouseEnter={() => handleMouseEnter(comment.id)}
                          onMouseLeave={(e) => handleMouseLeave(comment.id, e)}
                        >
                          {reactions.map((reaction, index) => (
                            <button
                              key={index}
                              className="channel-post-reaction-emoji"
                              onMouseDown={(e) => {
                                e.stopPropagation();
                                e.preventDefault();
                                handleCommentReaction(comment.id, reaction, e);
                              }}
                              onClick={(e) => {
                                e.stopPropagation();
                                e.preventDefault();
                              }}
                              disabled={reactingComments[comment.id]}
                            >
                              {(() => {
                                const isImagePath = typeof reaction === 'string' && (reaction.startsWith('/') || reaction.includes('.png') || reaction.includes('.jpg') || reaction.includes('.svg'));
                                return isImagePath ? (
                                  <img
                                    src={reaction}
                                    alt={reactionTypes[index]}
                                    style={{ width: "24px", height: "24px", objectFit: 'contain', display: 'inline-block', transform: 'scale(1.5)' }}
                                  />
                                ) : (
                                  <span>{reaction}</span>
                                );
                              })()}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                    {renderReactionBreakdown(comment.reaction_counts)}

                    {/* Reply Button */}
                    <button
                      className="channel-post-comment-reply-btn"
                      onClick={(e) => {
                        e.preventDefault();
                        toggleReplyInput(comment.id);
                      }}
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
                      </svg>
                      <span>{t("channelPostComments.reply", "Reply")}</span>
                    </button>

                    {/* Show Replies Button */}
                    {((comment.replies_count > 0) || (replies[comment.id] && replies[comment.id].length > 0)) && (
                      <button
                        className="channel-post-comment-reply-btn"
                        onClick={(e) => {
                          e.preventDefault();
                          toggleReplies(comment.id);
                        }}
                      >
                        {showReplies[comment.id]
                          ? t("channelPostComments.hideReplies", "Hide replies")
                          : t("channelPostComments.showReplies", `Show ${replies[comment.id]?.length || comment.replies_count || 0} ${(replies[comment.id]?.length || comment.replies_count || 0) === 1 ? 'reply' : 'replies'}`)}
                      </button>
                    )}

                    {/* Delete Button - Only show if user owns the comment */}
                    {isCommentOwner(comment) && (
                      <button
                        className="channel-post-comment-delete-btn"
                        onClick={(e) => {
                          e.preventDefault();
                          handleDeleteCommentClick(comment.id);
                        }}
                        disabled={deletingComments[comment.id]}
                        title={t("channelPostComments.delete", "Delete")}
                      >
                        {deletingComments[comment.id] ? (
                          <span>{t("channelPostComments.deleting", "Deleting...")}</span>
                        ) : (
                          <>
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <path d="M3 6h18M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                            </svg>
                            <span>{t("channelPostComments.delete", "Delete")}</span>
                          </>
                        )}
                      </button>
                    )}
                  </div>
                </div>

                {/* Replies Section */}
                {showReplies[comment.id] && replies[comment.id] && replies[comment.id].length > 0 && (
                  <div className="channel-post-replies-section">
                    {loadingReplies[comment.id] ? (
                      <div className="channel-post-replies-loading">
                        {t("channelPostComments.loading", "Loading...")}
                      </div>
                    ) : (
                      replies[comment.id].map((reply) => (
                        <div key={reply.id} className="channel-post-reply">
                          <img
                            src={reply.author.profileImage || getUserPlaceholderImage(reply.author)}
                            alt={reply.author.name}
                            className="channel-post-reply-avatar"
                            onError={(e) => {
                              e.target.src = getUserPlaceholderImage(comment.author);
                            }}
                          />
                          <div className="channel-post-reply-content">
                            <div className="channel-post-reply-header">
                              <span className="channel-post-reply-author">{reply.author.name}</span>
                              <span className="channel-post-reply-time">{reply.createdAt}</span>
                            </div>
                            <div
                              className="channel-post-reply-text"
                              dir={isArabicText(reply.content) ? 'rtl' : 'ltr'}
                            >
                              {formatContent(reply.content)}
                            </div>

                            {/* Reply Reactions */}
                            <div className="channel-post-reply-actions">
                              <div className="channel-post-reply-actions-wrapper">
                                <div
                                  ref={(el) => replyContainerRefs.current[reply.id] = el}
                                  className="channel-post-reply-reaction-container"
                                  onMouseEnter={() => handleReplyMouseEnter(reply.id)}
                                  onMouseLeave={(e) => handleReplyMouseLeave(reply.id, e)}
                                  style={{ position: 'relative' }}
                                >
                                  <button
                                    className={`channel-post-reply-like-btn ${reply.user_reaction_type ? 'has-reaction' : ''}`}
                                    onClick={(e) => {
                                      e.preventDefault();
                                      if (!showReplyReactionPicker[reply.id]) {
                                        handleReplyMouseEnter(reply.id);
                                      }
                                    }}
                                  >
                                    {reply.user_reaction_type && reactionTypeToEmoji[reply.user_reaction_type] ? (
                                      (() => {
                                        const emoji = reactionTypeToEmoji[reply.user_reaction_type];
                                        const isImagePath = typeof emoji === 'string' && (emoji.startsWith('/') || emoji.includes('.png') || emoji.includes('.jpg') || emoji.includes('.svg'));
                                        return isImagePath ? (
                                          <img
                                            src={emoji}
                                            alt={reply.user_reaction_type}
                                            style={{ width: "16px", height: "16px", objectFit: 'contain', display: 'inline-block', transform: 'scale(1.5)' }}
                                          />
                                        ) : (
                                          <span style={{ fontSize: '16px' }}>{emoji}</span>
                                        );
                                      })()
                                    ) : (
                                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"></path>
                                      </svg>
                                    )}
                                    <span>{reply.total_reactions || reply.likes || 0}</span>
                                  </button>

                                  {showReplyReactionPicker[reply.id] && (
                                    <div
                                      className="channel-post-reaction-picker"
                                      style={{ bottom: '100%', marginBottom: '8px' }}
                                      onMouseEnter={() => handleReplyMouseEnter(reply.id)}
                                      onMouseLeave={(e) => handleReplyMouseLeave(reply.id, e)}
                                    >
                                      {reactions.map((reaction, index) => (
                                        <button
                                          key={index}
                                          className="channel-post-reaction-emoji"
                                          onMouseDown={(e) => {
                                            e.stopPropagation();
                                            e.preventDefault();
                                            handleReplyReaction(reply.id, reaction, e, comment.id);
                                          }}
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            e.preventDefault();
                                          }}
                                          disabled={reactingReplies[reply.id]}
                                        >
                                          {(() => {
                                            const isImagePath = typeof reaction === 'string' && (reaction.startsWith('/') || reaction.includes('.png') || reaction.includes('.jpg') || reaction.includes('.svg'));
                                            return isImagePath ? (
                                              <img
                                                src={reaction}
                                                alt={reactionTypes[index]}
                                                style={{ width: "24px", height: "24px", objectFit: 'contain', display: 'inline-block', transform: 'scale(1.5)' }}
                                              />
                                            ) : (
                                              <span>{reaction}</span>
                                            );
                                          })()}
                                        </button>
                                      ))}
                                    </div>
                                  )}
                                </div>
                                {renderReactionBreakdown(reply.reaction_counts)}

                                {/* Delete Button for Reply - Only show if user owns the reply */}
                                {isCommentOwner(reply) && (
                                  <button
                                    className="channel-post-reply-delete-btn"
                                    onClick={(e) => {
                                      e.preventDefault();
                                      handleDeleteReplyClick(reply.id, comment.id);
                                    }}
                                    disabled={deletingReplies[reply.id]}
                                    title={t("channelPostComments.delete", "Delete")}
                                  >
                                    {deletingReplies[reply.id] ? (
                                      <span>{t("channelPostComments.deleting", "Deleting...")}</span>
                                    ) : (
                                      <>
                                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                          <path d="M3 6h18M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                                        </svg>
                                        <span>{t("channelPostComments.delete", "Delete")}</span>
                                      </>
                                    )}
                                  </button>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                )}

                {/* Reply Input Form - Show when replies are visible or reply button clicked */}
                {(showReplies[comment.id] || showReplyInput[comment.id]) && currentUser && (currentUser.id || currentUser.user_id) && (
                  <form
                    onSubmit={(e) => handleReplySubmit(comment.id, e)}
                    className="channel-post-reply-form"
                  >
                    <img
                      src={getProfileImageUrl() || getUserPlaceholderImage(currentUser)}
                      alt="Profile"
                      className="channel-post-reply-form-avatar"
                      onError={(e) => {
                        e.target.src = getUserPlaceholderImage(comment.author);
                      }}
                    />
                    <div className="channel-post-reply-form-input-container">
                      <input
                        type="text"
                        value={replyInputs[comment.id] || ''}
                        onChange={(e) => setReplyInputs(prev => ({ ...prev, [comment.id]: e.target.value }))}
                        placeholder={t(
                          "channelPostComments.replyPlaceholder",
                          "Write a reply..."
                        )}
                        className="channel-post-reply-form-input"
                        dir={isArabicText(replyInputs[comment.id] || '') ? 'rtl' : 'ltr'}
                      />
                      <button
                        type="submit"
                        className="channel-post-reply-form-submit"
                        disabled={!replyInputs[comment.id]?.trim() || submittingReplies[comment.id]}
                      >
                        {submittingReplies[comment.id]
                          ? t("channelPostComments.posting", "Posting...")
                          : t("channelPostComments.post", "Post")}
                      </button>
                    </div>
                  </form>
                )}
              </div>
            </div>
            ))}
            {/* Infinite scroll sentinel */}
            <div ref={loadMoreRef} className="channel-post-comments-sentinel">
              {isLoadingMore && (
                <div className="channel-post-comments-loading-more">
                  <div className="channel-post-comments-spinner-small"></div>
                  <span>{t("channelPostComments.loadingMore", "Loading more...")}</span>
                </div>
              )}
              {!hasMore && comments.length > 20 && (
                <div className="channel-post-comments-end">
                  {t("channelPostComments.allLoaded", "All comments loaded")}
                </div>
              )}
            </div>
          </>
        ) : (
          <div className="channel-post-comments-empty">
            <p>
              {t(
                "channelPostComments.empty",
                "No comments yet. Be the first to comment!"
              )}
            </p>
          </div>
        )}
      </div>

      <form onSubmit={handleSubmitComment} className="channel-post-comment-form">
        <img
          src={getProfileImageUrl() || getUserPlaceholderImage(currentUser)}
          alt="Profile"
          className="channel-post-comment-form-avatar"
          onError={(e) => {
            e.target.src = getUserPlaceholderImage(currentUser);
          }}
        />
        <div className="channel-post-comment-form-input-container">
          <input
            type="text"
            value={commentText}
            onChange={(e) => setCommentText(e.target.value)}
            placeholder={t(
              "channelPostComments.placeholder",
              "Write a comment..."
            )}
            className="channel-post-comment-form-input"
            dir={isArabicText(commentText) ? 'rtl' : 'ltr'}
          />
          <button
            type="submit"
            className="channel-post-comment-form-submit"
            disabled={!commentText.trim() || isSubmitting}
          >
            {isSubmitting
              ? t("channelPostComments.posting", "Posting...")
              : t("channelPostComments.post", "Post")}
          </button>
        </div>
      </form>

      {/* Delete Comment Confirmation Modal */}
      <ConfirmModal
        isOpen={deleteCommentModal.isOpen}
        title={t("channelPostComments.deleteTitle", "Delete Comment")}
        message={t(
          "channelPostComments.confirmDeleteComment",
          "Are you sure you want to delete this comment?"
        )}
        confirmLabel={t("channelPostComments.delete", "Delete")}
        cancelLabel={t("common.cancel", "Cancel")}
        onConfirm={handleDeleteCommentConfirm}
        onCancel={handleDeleteCommentCancel}
        showWarningIcon={true}
      />

      {/* Delete Reply Confirmation Modal */}
      <ConfirmModal
        isOpen={deleteReplyModal.isOpen}
        title={t("channelPostComments.deleteTitle", "Delete Reply")}
        message={t(
          "channelPostComments.confirmDeleteReply",
          "Are you sure you want to delete this reply?"
        )}
        confirmLabel={t("channelPostComments.delete", "Delete")}
        cancelLabel={t("common.cancel", "Cancel")}
        onConfirm={handleDeleteReplyConfirm}
        onCancel={handleDeleteReplyCancel}
        showWarningIcon={true}
      />

      {/* Error Modal */}
      <ConfirmModal
        isOpen={errorModal.isOpen}
        title={t("channelPostComments.errorTitle", "Error")}
        message={errorModal.message}
        confirmLabel={t("common.ok", "OK")}
        onConfirm={() => setErrorModal({ isOpen: false, message: '' })}
        onCancel={() => setErrorModal({ isOpen: false, message: '' })}
        showWarningIcon={true}
      />
    </div>
  );
};

export default ChannelPostComments;

